import { CoursePageFooter } from '@/components/course/CoursePageFooter'
import { CoursePagePremiumLoader } from '@/components/course/CoursePagePremiumLoader'
import { CoursesCatalogCard } from '@/components/courses/CoursesCatalogCard'
import { CoursesCatalogRow } from '@/components/courses/CoursesCatalogRow'
import { HelpScoutBeacon } from '@/components/HelpScoutBeacon'
import { InstructorHero } from '@/components/sections/InstructorHero'
import { Container, ErrorDisplay, SectionTitle } from '@/components/ui'
import { DashboardViewModeToggle } from '@/components/ui/DashboardViewModeToggle'
import { BodyText } from '@/components/ui/Typography'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useEffectiveMinStatus } from '@/hooks/useDraftViewer'
import { useAuth } from '@/lib/AuthContext'
import { BRAND_COLORS } from '@/lib/brandColors'
import { courseGridLayout } from '@/lib/courseGridLayout'
import { courseThumbnailUri } from '@/lib/courseThumbnail'
import { DASHBOARD_GRID_GAP } from '@/lib/dashboardToolbarUi'
import { CATALOG_STALE_TIME, queryKeys } from '@/lib/queryClient'
import {
  CONTENT_MAX_WIDTH,
  useCatalogProgressBarHeight,
  useIsMobile,
  useStableWindowWidth,
} from '@/lib/useDeviceType'
import {
  catalogRowToCourse,
  COURSE_CATALOG_BROWSE_LIMIT,
  searchCourseCatalog,
} from '@/supabase/services/courseCatalogSearch'
import {
  getCourseIdsByInstructorId,
  getInstructorBySlug,
  type InstructorListItem,
} from '@/supabase/services/instructors'
import { fetchUserCourseProgress } from '@/supabase/services/optimizedCourse'
import type { Course } from '@/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocalSearchParams } from 'expo-router'
import React, { useMemo } from 'react'
import { Platform } from 'react-native'
import { ScrollView, XStack, YStack } from 'tamagui'

const CONTAINER_PAD_X = 16
const GRID_GAP = DASHBOARD_GRID_GAP

export default function InstructorDetailScreen() {
  const params = useLocalSearchParams<{ slug: string }>()
  const slug = String(params.slug ?? '').trim()
  const validSlug = slug.length > 0

  const { user } = useAuth()
  const minStatus = useEffectiveMinStatus()
  const isMobile = useIsMobile()
  const stableWidth = useStableWindowWidth()
  const progressBarHeight = useCatalogProgressBarHeight()
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = usePersistedState<'grid' | 'list'>(
    'courses_view_mode',
    'grid',
  )

  const layout = useMemo(() => {
    const inner = Math.min(CONTENT_MAX_WIDTH, stableWidth) - CONTAINER_PAD_X * 2
    return courseGridLayout(inner)
  }, [stableWidth])

  const instructorQuery = useQuery({
    queryKey: queryKeys.instructors.bySlug(slug),
    queryFn: () => getInstructorBySlug(slug),
    enabled: validSlug,
    staleTime: CATALOG_STALE_TIME,
    initialData: () => {
      const seeded = queryClient.getQueryData<InstructorListItem>(
        queryKeys.instructors.bySlug(slug),
      )
      if (seeded) return seeded
      const all = queryClient.getQueryData<InstructorListItem[]>(
        queryKeys.instructors.all,
      )
      return all?.find((i) => i.slug === slug)
    },
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(queryKeys.instructors.all)?.dataUpdatedAt,
  })

  useDocumentTitle(instructorQuery.data?.name ?? null)

  const instructorId = instructorQuery.data?.id
  const courseIdsQuery = useQuery({
    queryKey: queryKeys.instructors.courseIds(instructorId ?? null),
    queryFn: () => getCourseIdsByInstructorId(instructorId!),
    enabled: !!instructorId,
    staleTime: CATALOG_STALE_TIME,
  })

  const catalogQuery = useQuery({
    queryKey: queryKeys.courses.catalogHub(user?.id ?? null, minStatus),
    queryFn: () =>
      searchCourseCatalog('', COURSE_CATALOG_BROWSE_LIMIT, user?.id, null, minStatus),
    staleTime: CATALOG_STALE_TIME,
  })

  const instructor = instructorQuery.data

  const courses = useMemo<Course[]>(() => {
    const ids = courseIdsQuery.data ?? []
    if (ids.length === 0) return []
    const byId = new Map(
      (catalogQuery.data ?? []).map((row) => [row.id, catalogRowToCourse(row)]),
    )
    return ids
      .map((id) => byId.get(id))
      .filter((c): c is Course => !!c)
  }, [courseIdsQuery.data, catalogQuery.data])

  const courseIds = useMemo(() => courses.map((c) => c.id), [courses])
  const { data: progressByCourseId = {} } = useQuery({
    queryKey: queryKeys.userLessons.courseProgress(user?.id ?? null, courseIds),
    queryFn: () => fetchUserCourseProgress(user?.id ?? null, courseIds),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!user?.id && courseIds.length > 0,
  })

  const isLoading =
    instructorQuery.isLoading || courseIdsQuery.isLoading || catalogQuery.isLoading
  const error =
    instructorQuery.error || courseIdsQuery.error || catalogQuery.error

  const refetchAll = () => {
    void instructorQuery.refetch()
    void courseIdsQuery.refetch()
    void catalogQuery.refetch()
  }

  if (!validSlug) {
    return (
      <YStack f={1} bg="$bg" ai="center" jc="center" px="$4">
        <BodyText color="$color">Instructor not found</BodyText>
      </YStack>
    )
  }

  if (isLoading) {
    return (
      <YStack f={1} bg="$bg">
        <HelpScoutBeacon />
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
        >
          <CoursePagePremiumLoader variant="course" />
        </ScrollView>
      </YStack>
    )
  }

  if (error) {
    return (
      <YStack f={1} bg="$bg">
        <ErrorDisplay error={error as Error} onRetry={refetchAll} />
      </YStack>
    )
  }

  if (!instructor) {
    return (
      <YStack f={1} bg="$bg" ai="center" jc="center" px="$4">
        <BodyText color="$color">Instructor not found</BodyText>
      </YStack>
    )
  }

  const heroAvatar = isMobile
    ? instructor.avatar_url_mobile ?? instructor.avatar_url_desktop
    : instructor.avatar_url_desktop ?? instructor.avatar_url_mobile

  return (
    <YStack f={1} bg="$bg">
      <HelpScoutBeacon />
      <ScrollView
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={{
          flexGrow: 1,
          ...(Platform.OS === 'web' ? { minHeight: '100%' } : {}),
          paddingBottom: Platform.OS === 'web' ? 0 : 16,
        }}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
      >
        <InstructorHero
          name={instructor.name}
          instrument={instructor.instrument}
          description={instructor.description}
          avatarUrl={heroAvatar}
        />

        <Container pt={32} pb="$5" gap={24} $gtSm={{ pt: 40 }}>
          <XStack ai="center" jc="space-between" flexWrap="wrap" gap="$2">
            <SectionTitle color={BRAND_COLORS.text}>Courses</SectionTitle>
            {courses.length > 0 ? (
              <DashboardViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                gap="$2"
              />
            ) : null}
          </XStack>
          {courses.length === 0 ? (
            <BodyText color={BRAND_COLORS.muted}>
              No courses available yet.
            </BodyText>
          ) : viewMode === 'list' ? (
            <YStack gap={GRID_GAP} width="100%">
              {courses.map((course) => (
                <CoursesCatalogRow
                  key={course.id}
                  course={course}
                  href={`/courses/${course.slug}`}
                  thumbnailUri={courseThumbnailUri(course)}
                  progressPercentage={(progressByCourseId[course.id] ?? 0) * 100}
                  progressBarHeight={progressBarHeight}
                  isMobile={isMobile}
                />
              ))}
            </YStack>
          ) : (
            <XStack
              flexWrap="wrap"
              columnGap={GRID_GAP}
              rowGap={GRID_GAP}
              jc={layout.justify === 'center' ? 'center' : 'flex-start'}
            >
              {courses.map((course) => (
                <YStack
                  key={course.id}
                  w={layout.cardWidth}
                  style={{ flexGrow: 0, flexShrink: 0 }}
                >
                  <CoursesCatalogCard
                    course={course}
                    href={`/courses/${course.slug}`}
                    width={layout.cardWidth}
                    thumbnailUri={courseThumbnailUri(course)}
                    progressPercentage={(progressByCourseId[course.id] ?? 0) * 100}
                    progressBarHeight={progressBarHeight}
                    isMobile={isMobile}
                  />
                </YStack>
              ))}
            </XStack>
          )}
        </Container>

        {Platform.OS === 'web' ? (
          <YStack flexGrow={1} minHeight={1} width="100%" />
        ) : null}
        <CoursePageFooter />
      </ScrollView>
    </YStack>
  )
}
